import { useRef, useCallback, useEffect, useEffectEvent, useState } from "react";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import { hitTestGraphic } from "@components/world-map/world-map-utils";
import { findClusterGraphic, getFeaturePoint, getHighlightSymbol, queryClusterMemberIds, setHiddenHighlightFeature } from "@components/world-map/helpers/world-map-highlight-helpers";
import { watch } from "@arcgis/core/core/reactiveUtils";

/**
 * Highlights a single marker on the world map by overlaying a darker version
 * of the marker icon on `view.graphics`. The overlay color tracks the active
 * map style so that highlights stay readable on every basemap.
 *
 * When the target feature is hidden inside a cluster, the overlay is placed at
 * the cluster's position instead.
 *
 * Supports a persistent popup highlight via `popupEntityId`: when the caller
 * clears the hover highlight, the hook automatically falls back to highlighting
 * the popup entity.
 */
export function useWorldMapHighlight(viewRef, layerRef, isViewReady, isMarkerLifted, onEntityHover, popupEntityId, onClusterHover) {
  const highlightState = useRef({
    overlayGraphic: null,
    latestRequestId: 0,
    currentHighlightedEntityId: null
  });
  const hoverState = useRef({
    isHitTestInFlight: false,
    shouldRetry: false,
    latestScreenPoint: null,
    frameHandle: null,
    lastEmittedId: null,
    pointerEntityId: null,
    lastClusterObjectId: null
  });

  /** Entity id set via the public `highlightEntity` API (null when no external hover). */
  const hoverEntityIdRef = useRef(null);
  const popupEntityIdRef = useRef(popupEntityId);
  popupEntityIdRef.current = popupEntityId;

  // Latest 3D marker lift state, read inside hover callbacks without re-creating
  // them, so overlay symbols match the grounded/lifted data markers.
  const isMarkerLiftedRef = useRef(isMarkerLifted);
  isMarkerLiftedRef.current = isMarkerLifted;

  // Map point where the DOM pulse ring is shown. Set for list/selected highlights
  // (the motion cue) and cleared for direct map hovers, which only recolor.
  const [pulseMapPoint, setPulseMapPoint] = useState(null);

  // GraphicsLayer that holds the highlight overlay. Used instead of `view.graphics`
  // so it can carry `relative-to-scene` elevation: in 3D the overlay then sits on the
  // same surface (rooftops/terrain top) as the data markers instead of being draped on
  // the ground and rendered inside the building layer. In 2D the elevation is ignored.
  const overlayLayerRef = useRef(null);

  /**
   * Returns the overlay GraphicsLayer, creating it (and adding it to the current
   * map) on first use. Re-created if the view/map was replaced by a 2D/3D toggle.
   */
  const getOverlayLayer = useCallback(() => {
    const map = viewRef.current?.map;
    if (!map) {
      return null;
    }
    if (!overlayLayerRef.current || !map.layers.includes(overlayLayerRef.current)) {
      overlayLayerRef.current = new GraphicsLayer({
        elevationInfo: {
          mode: "relative-to-scene"
        }
      });
      map.add(overlayLayerRef.current);
    }
    return overlayLayerRef.current;
  }, [viewRef]);
  const clearOverlay = useCallback(() => {
    const {
      overlayGraphic
    } = highlightState.current;
    if (overlayGraphic) {
      overlayLayerRef.current?.remove(overlayGraphic);
    }
    highlightState.current.overlayGraphic = null;
    setPulseMapPoint(null);

    // Restore the underlying data-layer marker that was hidden behind the overlay.
    const view = viewRef.current;
    const layer = layerRef.current;
    if (view && layer) {
      void setHiddenHighlightFeature(view, layer, null);
    }
  }, [viewRef, layerRef]);
  const applyOverlay = useCallback(async (entityId, requestId) => {
    const view_0 = viewRef.current;
    const layer_0 = layerRef.current;
    if (!view_0 || !layer_0) {
      return;
    }
    const is3D = view_0.type === "3d";
    try {
      const geometry = await getFeaturePoint(layer_0, entityId);
      if (requestId !== highlightState.current.latestRequestId || !geometry) {
        return;
      }
      const clusterGraphic = await findClusterGraphic(view_0, layer_0, geometry);
      if (requestId !== highlightState.current.latestRequestId) {
        return;
      }
      const overlayPoint = clusterGraphic ? clusterGraphic.geometry : geometry;

      // A highlight is a "transient hover" when the targeted marker is the one
      // currently under the pointer on the map and isn't the selected popup
      // entity. It grows + recolors like other highlights, but without the pulse
      // ring (that stays reserved for list/selected highlights below).
      const isTransientHover = entityId === hoverState.current.pointerEntityId && entityId !== popupEntityIdRef.current;
      const graphic = new Graphic({
        geometry: overlayPoint,
        symbol: getHighlightSymbol(undefined, is3D, isMarkerLiftedRef.current)
      });
      getOverlayLayer()?.add(graphic);
      highlightState.current.overlayGraphic = graphic;
      if (isTransientHover) {
        setPulseMapPoint(null);
        return;
      }

      // List/selected highlight → recolor stays static on the map (perfectly
      // aligned, incl. 3D lift + callout) and a DOM pulse ring is shown at the
      // marker position for the motion cue.
      setPulseMapPoint(overlayPoint);

      // Hide the original standalone marker so it doesn't show behind the
      // enlarged overlay. Clustered features aren't rendered individually.
      if (!clusterGraphic) {
        void setHiddenHighlightFeature(view_0, layer_0, entityId);
      }
    } catch {
      // Silently ignore — the layer or view may have been destroyed during navigation
    }
  }, [viewRef, layerRef, getOverlayLayer]);
  const setActiveHighlight = useCallback(entityId_0 => {
    if (entityId_0 === highlightState.current.currentHighlightedEntityId) {
      return;
    }
    highlightState.current.currentHighlightedEntityId = entityId_0;
    highlightState.current.latestRequestId += 1;
    clearOverlay();
    if (!isViewReady || !entityId_0) {
      return;
    }
    void applyOverlay(entityId_0, highlightState.current.latestRequestId);
  }, [clearOverlay, isViewReady, applyOverlay]);
  const highlightEntity = useCallback(entityId_1 => {
    hoverEntityIdRef.current = entityId_1;
    setActiveHighlight(entityId_1 ?? popupEntityIdRef.current ?? null);
  }, [setActiveHighlight]);

  /**
   * Syncs popup-driven highlight with map state.
   *
   * Only applies when there is no active external hover (`hoverEntityIdRef`), so popup
   * state never overrides an explicit hover coming from the list or map interactions.
   */
  useEffect(() => {
    if (hoverEntityIdRef.current) {
      return;
    }
    setActiveHighlight(popupEntityId ?? null);
  }, [popupEntityId, setActiveHighlight]);

  // Re-apply the active highlight overlay symbol when the map style changes or the
  // 3D markers switch between grounded and lifted, so the overlay stays consistent
  // with the data markers.
  useEffect(() => {
    const {
      overlayGraphic: overlayGraphic_0
    } = highlightState.current;
    if (overlayGraphic_0) {
      overlayGraphic_0.symbol = getHighlightSymbol(undefined, viewRef.current?.type === "3d", isMarkerLifted);
    }
  }, [viewRef, isMarkerLifted]);

  /**
   * Recomputes the current highlight after map navigation settles.
   *
   * Cluster membership and centroid positions can change after zoom/pan, so this forces
   * a fresh overlay resolution for the currently tracked highlighted entity.
   */
  useEffect(() => {
    if (!isViewReady || !viewRef.current) {
      return;
    }
    const handle = watch(() => viewRef.current?.stationary, isStationary => {
      if (!isStationary) {
        return;
      }
      const entityId_2 = highlightState.current.currentHighlightedEntityId;
      if (!entityId_2) {
        return;
      }
      // Force re-computation by resetting the tracked id
      highlightState.current.currentHighlightedEntityId = null;
      setActiveHighlight(entityId_2);
    });
    return () => handle.remove();
  }, [isViewReady, viewRef, setActiveHighlight]);
  const emitHoveredEntity = useEffectEvent(entityId_3 => {
    if (entityId_3 === hoverState.current.lastEmittedId) {
      return;
    }
    hoverState.current.lastEmittedId = entityId_3;
    onEntityHover?.(entityId_3);
  });

  /**
   * Handles pointer hover processing for map graphics.
   *
   * Flow:
   * - Runs a cluster-first hit test and renders cluster overlay when applicable.
   * - Queries cluster members and emits them through `onClusterHover`.
   * - Falls back to standalone marker hit testing and emits a single entity id.
   * - Uses in-flight/retry guards from `hoverState` to avoid overlapping async hit tests.
   */
  const handlePointerMove = useEffectEvent(async () => {
    const screenPoint = hoverState.current.latestScreenPoint;
    if (!viewRef.current || !screenPoint) {
      return;
    }
    if (hoverState.current.isHitTestInFlight) {
      hoverState.current.shouldRetry = true;
      return;
    }
    hoverState.current.isHitTestInFlight = true;
    try {
      // Check cluster first — place a blue[800] pin overlay at its center.
      const clusterGraphic = await hitTestGraphic(viewRef.current, screenPoint, layerRef.current, true);
      if (clusterGraphic) {
        const clusterId = clusterGraphic.getObjectId();

        // Pointer is over a cluster, not a standalone marker.
        hoverState.current.pointerEntityId = null;

        // Still hovering the same cluster — skip redundant work.
        if (clusterId !== null && clusterId !== undefined && clusterId === hoverState.current.lastClusterObjectId) {
          return;
        }
        hoverState.current.lastClusterObjectId = clusterId ?? null;
        const clusterPoint = clusterGraphic.geometry;
        clearOverlay();
        // Direct map hover just recolors the cluster — no animation.
        const graphic = new Graphic({
          geometry: clusterPoint,
          symbol: getHighlightSymbol(1)
        });
        getOverlayLayer()?.add(graphic);
        highlightState.current.overlayGraphic = graphic;
        highlightState.current.currentHighlightedEntityId = "__cluster__";

        // Mark as cluster so the next non-cluster emission won't be deduplicated.
        hoverState.current.lastEmittedId = "__cluster__";
        const memberIds = await queryClusterMemberIds(viewRef.current, layerRef.current, clusterGraphic);
        onClusterHover?.(memberIds);
        return;
      }

      // Left a cluster — reset the dedup tracker.
      hoverState.current.lastClusterObjectId = null;

      // Check standalone marker.
      const graphic = await hitTestGraphic(viewRef.current, screenPoint, layerRef.current, false);
      const entityId = graphic ? String(graphic.attributes?.id) : null;

      // Track the standalone marker under the pointer so list/selected highlights
      // can be told apart from direct map hovers (which must not animate).
      hoverState.current.pointerEntityId = entityId;

      // When moving off a cluster onto empty space, clear the cluster overlay.
      if (!entityId && highlightState.current.currentHighlightedEntityId === "__cluster__") {
        clearOverlay();
        highlightState.current.currentHighlightedEntityId = null;
      }
      emitHoveredEntity(entityId);
    } finally {
      hoverState.current.isHitTestInFlight = false;
      if (hoverState.current.shouldRetry) {
        hoverState.current.shouldRetry = false;
        void handlePointerMove();
      }
    }
  });
  const scheduleHitTest = useEffectEvent(() => {
    if (hoverState.current.frameHandle !== null) {
      return;
    }
    hoverState.current.frameHandle = window.requestAnimationFrame(() => {
      hoverState.current.frameHandle = null;
      void handlePointerMove();
    });
  });

  /**
   * Registers pointer listeners used to drive hover hit testing.
   *
   * `pointer-move` stores the latest screen coordinates and schedules a frame-throttled
   * hit test. `pointer-leave` clears the emitted hover state.
   */
  useEffect(() => {
    if (!isViewReady || !viewRef.current || !onEntityHover) {
      return;
    }
    const hover = hoverState.current;
    const handle_0 = viewRef.current.on("pointer-move", event => {
      hover.latestScreenPoint = {
        x: event.x,
        y: event.y
      };
      scheduleHitTest();
    });
    const leaveHandle = viewRef.current.on("pointer-leave", () => {
      hoverState.current.pointerEntityId = null;
      emitHoveredEntity(null);
    });
    return () => {
      handle_0.remove();
      leaveHandle.remove();
      const frameHandle = hover.frameHandle;
      if (frameHandle !== null) {
        window.cancelAnimationFrame(frameHandle);
      }
    };
  }, [isViewReady, viewRef, onEntityHover]);

  /**
   * Ensures any remaining overlay graphic is removed when this hook unmounts.
   */
  useEffect(() => {
    return () => clearOverlay();
  }, [clearOverlay]);
  return {
    highlightEntity,
    pulseMapPoint
  };
}